/**
 * Phase A5 — /admin/users/:role/:id
 *
 * User detail page with two account actions:
 *   1. Send password reset email   (primary, no justification)
 *   2. Generate temporary password (secondary, justification required,
 *      plaintext shown ONCE in modal)
 *
 * Also renders the last-10 admin_password_actions for this user.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
    adminLogout, adminMe,
    adminSendReset, adminGenerateTemp, adminUserDetail,
} from "../lib/admin";

import "./Admin.css";
import "./AdminUsers.css";

export default function AdminUserDetail() {
    const navigate = useNavigate();
    const { role, id } = useParams();
    const [me, setMe] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // UI states
    const [showSendConfirm, setShowSendConfirm] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);
    const [toast, setToast] = useState("");

    const [tempPanelOpen, setTempPanelOpen] = useState(false);
    const [justification, setJustification] = useState("");
    const [generatingTemp, setGeneratingTemp] = useState(false);
    const [tempResult, setTempResult] = useState(null); // { temp_password, expires_at }
    const [copiedTemp, setCopiedTemp] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const [meR, d] = await Promise.all([
                adminMe(),
                adminUserDetail(role, id),
            ]);
            setMe(meR);
            setDetail(d);
        } catch (e) {
            if (e?.response?.status === 401) {
                navigate("/admin/login");
                return;
            }
            if (e?.response?.status === 404) {
                setErr("User not found.");
            } else {
                setErr("Couldn't load this user. Try again.");
            }
        } finally {
            setLoading(false);
        }
    }, [role, id, navigate]);

    useEffect(() => { load(); }, [load]);

    const onSignOut = async () => {
        try { await adminLogout(); } catch { /* no-op */ }
        navigate("/admin/login");
    };

    const handleSendReset = async () => {
        setSendingReset(true);
        try {
            await adminSendReset(role, id);
            setToast("Reset email sent.");
            setShowSendConfirm(false);
            await load();
            setTimeout(() => setToast(""), 4000);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "cannot_target_self") {
                setErr("You can't run account actions against your own account.");
            } else {
                setErr("Failed to send reset email.");
            }
            setShowSendConfirm(false);
        } finally {
            setSendingReset(false);
        }
    };

    const handleGenerateTemp = async () => {
        if (justification.trim().length < 5) return;
        setGeneratingTemp(true);
        try {
            const res = await adminGenerateTemp(role, id, justification.trim());
            setTempResult(res);
            setTempPanelOpen(false);
            setJustification("");
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "cannot_target_self") {
                setErr("You can't run account actions against your own account.");
            } else if (detail === "justification_required") {
                setErr("Justification needs to be at least 5 characters.");
            } else {
                setErr("Failed to generate a temporary password.");
            }
        } finally {
            setGeneratingTemp(false);
        }
    };

    const copyTemp = () => {
        if (!tempResult?.temp_password) return;
        navigator.clipboard.writeText(tempResult.temp_password);
        setCopiedTemp(true);
        setTimeout(() => setCopiedTemp(false), 2000);
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleString(undefined, {
                month: "short", day: "numeric", year: "numeric",
                hour: "numeric", minute: "2-digit",
            });
        } catch { return iso; }
    };

    const actionLabel = (t) => ({
        send_reset: "Sent reset email",
        generate_temp: "Generated temp password",
        attempt_failed: "Failed attempt",
    }[t] || t);

    return (
        <div className="admin-page" data-testid="admin-user-detail">
            <nav className="admin-nav">
                <div style={{ display: "flex", alignItems: "center", gap: "2rem", flexWrap: "wrap" }}>
                    <div className="logo-mark" onClick={() => navigate("/")}>
                        Laguna<span>.</span>
                    </div>
                    <div className="admin-bread">
                        <span
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate("/admin/users")}
                        >Users</span>
                        <span className="current">{detail?.email || id}</span>
                    </div>
                </div>
                <div className="admin-session">
                    <span>{me?.email}</span>
                    <button
                        className="signout"
                        onClick={onSignOut}
                        data-testid="admin-signout"
                    >Sign out</button>
                </div>
            </nav>

            <div className="admin-wrap">
                {loading && <div className="admin-users-status">Loading…</div>}
                {err && !loading && (
                    <div className="admin-users-error" data-testid="admin-user-error">
                        {err}
                    </div>
                )}
                {detail && !loading && (
                    <>
                        <div className="section-kicker">User detail</div>
                        <h1 className="admin-h1">
                            {detail.email}{" "}
                            <em className="admin-users-role-inline">
                                ({detail.role})
                            </em>
                        </h1>
                        <div className="admin-user-facts" data-testid="admin-user-facts">
                            <div>
                                <div className="mono admin-fact-label">ID</div>
                                <div className="admin-fact-value">{detail.id}</div>
                            </div>
                            <div>
                                <div className="mono admin-fact-label">Password</div>
                                <div>
                                    {detail.has_password
                                        ? <span className="admin-badge ok">Set</span>
                                        : <span className="admin-badge muted">Not set</span>}
                                </div>
                            </div>
                            <div>
                                <div className="mono admin-fact-label">Must change</div>
                                <div>
                                    {detail.must_change_password
                                        ? <span className="admin-badge warn">Yes</span>
                                        : <span className="admin-badge muted">No</span>}
                                </div>
                            </div>
                            <div>
                                <div className="mono admin-fact-label">Last sign-in</div>
                                <div>{fmt(detail.last_signin_at)}</div>
                            </div>
                            {detail.temp_password_expires_at && (
                                <div>
                                    <div className="mono admin-fact-label">Temp pw expires</div>
                                    <div>{fmt(detail.temp_password_expires_at)}</div>
                                </div>
                            )}
                        </div>

                        <h2 className="admin-user-h2">Account actions</h2>
                        <div className="admin-actions">
                            <button
                                className="admin-btn primary"
                                onClick={() => setShowSendConfirm(true)}
                                data-testid="admin-user-send-reset"
                            >
                                Send password reset email
                            </button>
                            <button
                                className="admin-btn secondary"
                                onClick={() => setTempPanelOpen((v) => !v)}
                                data-testid="admin-user-open-temp"
                            >
                                Generate temporary password
                            </button>
                        </div>

                        {tempPanelOpen && (
                            <div className="admin-users-temp-panel" data-testid="admin-user-temp-panel">
                                <label className="admin-users-label">
                                    Justification
                                    <span className="admin-users-label-hint">
                                        (required, min 5 chars — logged permanently)
                                    </span>
                                </label>
                                <textarea
                                    className="admin-users-textarea"
                                    rows={3}
                                    value={justification}
                                    onChange={(e) => setJustification(e.target.value)}
                                    placeholder="e.g., User called in, identity verified via DOB + last 4 of SSN"
                                    data-testid="admin-user-justification"
                                />
                                <div className="admin-users-warning">
                                    This action is logged permanently. The user
                                    will get a notification email. Share the
                                    temp password out-of-band — it will only
                                    be shown once.
                                </div>
                                <div className="admin-actions">
                                    <button
                                        className="admin-btn primary"
                                        disabled={justification.trim().length < 5 || generatingTemp}
                                        onClick={handleGenerateTemp}
                                        data-testid="admin-user-generate-temp-submit"
                                    >
                                        {generatingTemp ? "Generating…" : "Generate"}
                                    </button>
                                    <button
                                        className="admin-btn secondary"
                                        onClick={() => {
                                            setTempPanelOpen(false);
                                            setJustification("");
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {toast && (
                            <div className="admin-users-toast" data-testid="admin-user-toast">
                                {toast}
                            </div>
                        )}

                        <h2 className="admin-user-h2">Recent account actions</h2>
                        <table className="admin-users-table admin-users-actions-table"
                               data-testid="admin-user-actions-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    <th>Admin</th>
                                    <th>Justification</th>
                                    <th>When</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.recent_actions.length === 0 ? (
                                    <tr>
                                        <td colSpan={5}
                                            className="admin-users-status"
                                            style={{ textAlign: "center" }}>
                                            No actions logged yet.
                                        </td>
                                    </tr>
                                ) : detail.recent_actions.map((a) => (
                                    <tr key={a.id} data-testid={`admin-user-action-${a.id}`}>
                                        <td>{actionLabel(a.action_type)}</td>
                                        <td>{a.admin_email}</td>
                                        <td>{a.justification || "—"}</td>
                                        <td>{fmt(a.created_at)}</td>
                                        <td>
                                            {a.action_type === "generate_temp"
                                                ? (a.used_at
                                                    ? <span className="admin-badge ok">Used {fmt(a.used_at)}</span>
                                                    : <span className="admin-badge muted">Not yet used</span>)
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* Send-reset confirmation modal */}
            {showSendConfirm && (
                <div className="admin-users-modal-backdrop" data-testid="admin-user-send-modal">
                    <div className="admin-users-modal">
                        <h3>Send password reset email?</h3>
                        <p>
                            We'll email <strong>{detail?.email}</strong> a
                            reset link good for 30 minutes, plus a notification
                            that Success Team initiated it.
                        </p>
                        <div className="admin-actions">
                            <button
                                className="admin-btn primary"
                                onClick={handleSendReset}
                                disabled={sendingReset}
                                data-testid="admin-user-send-reset-confirm"
                            >
                                {sendingReset ? "Sending…" : "Yes, send reset"}
                            </button>
                            <button
                                className="admin-btn secondary"
                                onClick={() => setShowSendConfirm(false)}
                                disabled={sendingReset}
                            >Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Temp-password display modal — visible exactly once */}
            {tempResult && (
                <div className="admin-users-modal-backdrop" data-testid="admin-user-temp-modal">
                    <div className="admin-users-modal admin-users-modal-temp">
                        <h3>Temporary password generated</h3>
                        <p>
                            Share this with <strong>{detail?.email}</strong>{" "}
                            out-of-band (phone, in-person, encrypted channel —
                            not email). It will <strong>NOT</strong> be shown
                            again.
                        </p>
                        <div className="admin-users-temp-value mono"
                             data-testid="admin-user-temp-value">
                            {tempResult.temp_password}
                        </div>
                        <div className="admin-users-temp-meta">
                            Expires {fmt(tempResult.expires_at)}
                        </div>
                        <div className="admin-actions">
                            <button
                                className="admin-btn primary"
                                onClick={copyTemp}
                                data-testid="admin-user-temp-copy"
                            >
                                {copiedTemp ? "Copied ✓" : "Copy to clipboard"}
                            </button>
                            <button
                                className="admin-btn secondary"
                                onClick={() => setTempResult(null)}
                                data-testid="admin-user-temp-close"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
